import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { has, isNull } from 'lodash-es'

import { useTimeZone } from '../../../hooks'
import {
  getDateRangeParam,
  getListType,
  getRelatedFilters,
  isDateFilterType,
  isMultiselectFilterType,
  isNumberRangeFilterType,
  isRatingFilterName,
  isSelectFilterType,
  isToggleFilterType,
} from '../../../utils/filter'
import { Accordion, AccordionItem } from '../../Accordion'
import Button from '../../Button'
import { CustomSelect } from '../../CustomSelect'
import { DatePickerWithRange } from '../../DateTime'
import { IntegerInput } from '../../Input'
import { getFilterSingleFieldData } from '../Filter.api'
import {
  FILTER_LIST_ITEM,
  FILTER_LIST_ITEM_PROPS_GETTER,
} from '../Filter.constants'
import { FilterToggle } from '../FilterToggle'
import { FilterValue } from '../FilterValue'

import {
  QA_NAME_FILTER_NUMBER_RANGE_MAX,
  QA_NAME_FILTER_NUMBER_RANGE_MIN,
} from './FilterModalAvailableFilters.constants'
import {
  getDateRangeFilterParams,
  getFilterSelectTypeValues,
  getFilterValues,
  getMultiSelectButtonType,
  getMultiSelectFilterCount,
  getNumberRangeFilterParams,
  getOptionLabel,
  getOptionValue,
  parseApiDateToUkDay,
  toNumberRangeBound,
} from './FilterModalAvailableFilters.utils'

import * as styles from './FilterModalAvailableFilters.module.css'

function FilterModalAvailableFilters({
  apiUrl,
  availableFilters,
  entity,
  filterOptions,
  initialRangeFilterNames,
  isLoading,
  permanentFilters,
  shouldAdaptToMobileScreen,
  singleFieldApiParams,
  onClear,
  onClick,
  onOpenDateRange,
  onSetDateRange,
}) {
  const { t } = useTranslation()

  const timezone = useTimeZone()

  const relatedFilters = getRelatedFilters(availableFilters)

  function getItems(search, apiParams, options) {
    const params = {
      ...(search ? { search } : null),
      ...(permanentFilters ? permanentFilters : null),
      ...singleFieldApiParams,
    }

    return getFilterSingleFieldData({
      apiUrl,
      entity,
      options,
      params: {
        ...apiParams,
        ...params,
      },
    })
  }

  function getHandleClickExtraItem(name) {
    return onClose => {
      onOpenDateRange(name)

      onClose()
    }
  }

  return (
    <Accordion
      className={classNames(styles.item, {
        [styles.mobile]: shouldAdaptToMobileScreen,
      })}
      disabled={isLoading}
      doubleClassName={classNames(styles.doubleItem, {
        [styles.mobile]: shouldAdaptToMobileScreen,
      })}
      entity={entity}
      query={filterOptions}
      shouldAdaptToMobileScreen={shouldAdaptToMobileScreen}
      variant='style2'
      onClear={onClear}
    >
      {availableFilters
        .map(filter => {
          const {
            name,
            placeholder,
            query_param,
            search_min_length,
            search_param,
            single_field_query_param,
            title,
            type,
          } = filter

          if (isSelectFilterType(type)) {
            const filterValues = getFilterSelectTypeValues(name, filterOptions)
            const filtersCount = filterValues.length
            const listType = getListType(filter, entity)

            return (
              <AccordionItem
                accordionTitle={t(title)}
                key={name}
                staticCount={filtersCount}
              >
                <CustomSelect
                  autoFocus
                  activeItems={filterValues}
                  // Static options resolve locally, not via the endpoint.
                  // TODO: drop `options` once every facet is served by the endpoint.
                  api={filter.options ? undefined : getItems}
                  apiParams={{ field_name: single_field_query_param }}
                  buttonClassName={styles.button}
                  buttonHeight={40}
                  buttonType='select'
                  disabled={isLoading}
                  entity={entity}
                  listType={listType}
                  options={filter.options}
                  placeholder={t(placeholder)}
                  onClick={value => onClick(name, value, type)}
                />
                <FilterValue
                  disabled={isLoading}
                  entity={entity}
                  filter={filter}
                  filterOptions={filterOptions}
                  relatedFilters={relatedFilters}
                  onClear={onClear}
                  onClick={onClick}
                />
              </AccordionItem>
            )
          }

          if (isMultiselectFilterType(type)) {
            const listType = getListType(filter, entity)

            const filterValues = getFilterValues(name, filterOptions)

            const filtersCount = getMultiSelectFilterCount(
              name,
              filterOptions,
              filterValues
            )

            if (isRatingFilterName(name)) {
              return (
                <AccordionItem
                  accordionTitle={t(title)}
                  key={name}
                  staticCount={filtersCount}
                >
                  <CustomSelect
                    autoFocus
                    isMulti
                    activeItems={filterValues}
                    api={getItems}
                    apiParams={{ field_name: single_field_query_param }}
                    buttonClassName={classNames(styles.button, {
                      [styles.mobile]: shouldAdaptToMobileScreen,
                    })}
                    buttonHeight={40}
                    buttonType='outlined'
                    disabled={isLoading}
                    listType={listType}
                    placeholder={t(placeholder)}
                    onClick={value => onClick(name, value, type)}
                  />
                  <FilterValue
                    disabled={isLoading}
                    entity={entity}
                    filter={filter}
                    filterOptions={filterOptions}
                    relatedFilters={relatedFilters}
                    onClear={onClear}
                    onClick={onClick}
                    onSetDateRange={onSetDateRange}
                  />
                </AccordionItem>
              )
            }

            return (
              <AccordionItem
                accordionTitle={t(title)}
                key={name}
                staticCount={filtersCount}
              >
                <CustomSelect
                  autoFocus
                  isMulti
                  ExtraItem={name.includes('_range') ? Button.Text : null}
                  ListItem={FILTER_LIST_ITEM[name]}
                  activeItems={filterValues}
                  // TODO: remove when the backend serves the rental_listings single-field alias
                  api={filter.options ? undefined : getItems}
                  apiParams={{ field_name: single_field_query_param }}
                  buttonClassName={styles.button}
                  buttonHeight={40}
                  buttonType={getMultiSelectButtonType(name)}
                  disabled={isLoading}
                  entity={entity}
                  extraItemProps={{
                    className: styles.extraItem,
                    text: t('AddCustomDateBtnKey'),
                    onClick: getHandleClickExtraItem(name),
                  }}
                  getLabel={option =>
                    getOptionLabel({ entity, listType, name, option, t })
                  }
                  getListItemProps={FILTER_LIST_ITEM_PROPS_GETTER[name]}
                  getValue={getOptionValue}
                  inputHeight={40}
                  listType={listType}
                  minSearchString={search_min_length}
                  options={filter.options}
                  placeholder={t(placeholder)}
                  searchKey={search_param}
                  updateRect={filtersCount}
                  onClick={value => onClick(name, value, type)}
                />
                <FilterValue
                  disabled={isLoading}
                  entity={entity}
                  filter={filter}
                  filterOptions={filterOptions}
                  relatedFilters={relatedFilters}
                  onClear={onClear}
                  onClick={onClick}
                  onSetDateRange={onSetDateRange}
                />
              </AccordionItem>
            )
          }

          if (isNumberRangeFilterType(type)) {
            const {
              endParam,
              filtersCount,
              initialEnd,
              initialStart,
              startParam,
            } = getNumberRangeFilterParams(name, filterOptions)

            // `placeholder` may be a single string or a [min, max] pair.
            const [minPlaceholder, maxPlaceholder] = [placeholder].flat()

            return (
              <AccordionItem
                accordionTitle={t(title)}
                key={name}
                staticCount={filtersCount}
              >
                <div className={styles.numberRange}>
                  <IntegerInput
                    aria-label={minPlaceholder ? t(minPlaceholder) : undefined}
                    disabled={isLoading}
                    placeholder={minPlaceholder ? t(minPlaceholder) : undefined}
                    qaName={QA_NAME_FILTER_NUMBER_RANGE_MIN}
                    value={initialStart}
                    onChange={value =>
                      onClick(startParam, toNumberRangeBound(value), type)
                    }
                  />
                  <IntegerInput
                    aria-label={maxPlaceholder ? t(maxPlaceholder) : undefined}
                    disabled={isLoading}
                    placeholder={maxPlaceholder ? t(maxPlaceholder) : undefined}
                    qaName={QA_NAME_FILTER_NUMBER_RANGE_MAX}
                    value={initialEnd}
                    onChange={value =>
                      onClick(endParam, toNumberRangeBound(value), type)
                    }
                  />
                </div>
              </AccordionItem>
            )
          }

          if (isToggleFilterType(type)) {
            return (
              <FilterToggle
                className={classNames(styles.toggleItem, {
                  [styles.mobile]: shouldAdaptToMobileScreen,
                })}
                disabled={isLoading}
                filter={filter}
                isOn={has(filterOptions, name)}
                key={name}
                onClick={value => onClick(name, value, type)}
              />
            )
          }

          if (isDateFilterType(type)) {
            const hasRelatedFilter = has(
              relatedFilters,
              getDateRangeParam(name)
            )

            if (hasRelatedFilter) {
              return null
            }

            const {
              endDateParam,
              filtersCount,
              initialEnd,
              initialStart,
              startDateParam,
            } = getDateRangeFilterParams(name, filterOptions)

            const startDate = parseApiDateToUkDay(initialStart, timezone)
            const endDate = parseApiDateToUkDay(initialEnd, timezone)

            return (
              <AccordionItem
                accordionTitle={t(title)}
                key={name}
                staticCount={filtersCount}
              >
                <DatePickerWithRange
                  disabled={isLoading}
                  endValue={endDate}
                  initialIsRange={initialRangeFilterNames.includes(name)}
                  placeholder={t(placeholder)}
                  startValue={startDate}
                  value={startDate}
                  onChange={value => onClick(query_param, value, type)}
                  onChangeRange={([startValue, endValue]) =>
                    onSetDateRange(
                      startDateParam,
                      startValue,
                      endDateParam,
                      endValue,
                      type
                    )
                  }
                />
                <FilterValue
                  disabled={isLoading}
                  entity={entity}
                  filter={filter}
                  filterOptions={filterOptions}
                  relatedFilters={relatedFilters}
                  onClear={onClear}
                  onClick={onClick}
                  onSetDateRange={onSetDateRange}
                />
              </AccordionItem>
            )
          }

          return null
        })
        .filter(item => !isNull(item))}
    </Accordion>
  )
}

export default FilterModalAvailableFilters
